'use client'

import { FormError, FormLabel } from '@/components/ui/form'
import { Eye, EyeOff } from 'lucide-react'
import * as React from 'react'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type?:
    | 'text'
    | 'password'
    | 'search'
    | 'date'
    | 'email'
    | 'number'
    | 'tel'
    | 'color'
    | 'url'
    | 'file'
    | 'datetime-local'
    | 'time'
  icon?: React.ReactNode
  error?: string | undefined
  label?: string
  required?: boolean
  helpertext?: string
  info?: string
  hideErrorMessage?: boolean
}

const Input = React.memo(
  React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, label, required, icon, error, type, info, hideErrorMessage, ...props }, ref) => {
      const containerClassNames = React.useMemo(() => {
        const baseClasses =
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-sm border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
        const errorClass = error
          ? 'border-[0.5px] border-red-500'
          : 'focus-within:border focus-within:border-[1px] focus-within:border-blue-500'
        return `${baseClasses} ${errorClass} ${className}`
      }, [error, className])

      const [show, setShow] = React.useState(false)

      return (
        <div>
          {label ? (
            <FormLabel required={required} info={info}>
              {label}
            </FormLabel>
          ) : null}

          <div
            className={containerClassNames}
            style={{
              paddingLeft: type === 'file' ? '2px' : 'auto',
            }}
          >
            {icon && <div className='flex items-center mr-2 text-muted-foreground h-4 w-4 my-auto'>{icon}</div>}
            <input
              type={type === 'password' ? (show ? 'text' : 'password') : (type ?? 'text')}
              className='w-full active:border-none focus:outline-none active:outline-none bg-transparent  placeholder:text-[14px] placeholder:font-light font-light'
              ref={ref}
              style={{
                paddingTop: type === 'file' ? '3px' : '0',
              }}
              spellCheck
              lang='en-AU'
              {...props}
            />
            <div
              onClick={() => {
                setShow((prev) => !prev)
              }}
              style={{
                display: type === 'password' ? 'flex' : 'none',
              }}
              className='items-center  cursor-pointer'
            >
              {show ? <Eye className='w-3 h-3' /> : <EyeOff className='w-3 h-3' />}
            </div>
          </div>
          {hideErrorMessage ? null : <FormError error={error} />}
          {props?.helpertext ? <small className='text-muted-foreground'>{props?.helpertext}</small> : null}
        </div>
      )
    }
  )
)

export interface TextareaProps extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  error?: string | undefined
  label?: string
  required?: boolean
  height?: string
  helpertext?: string
  info?: string
  jsx?: React.ReactNode
}

const TextArea = React.memo(
  React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className, label, required, error, info, height, jsx, ...props }, ref) => {
      const containerClassNames = React.useMemo(() => {
        const baseClasses =
          'flex  rounded-sm w-full dark:bg-input/30 border p-2 text-[14px] font-light placeholder:text-[14px] file:font-medium disabled:cursor-not-allowed disabled:opacity-50 shadow-xs transition-[color,box-shadow] '
        const errorClass = error
          ? 'border-[.1px] border-red-600'
          : 'focus-within:border focus-within:border-[1px] focus-within:border-blue-500'
        return `${baseClasses} ${errorClass} ${className}`
      }, [error, className])

      return (
        <div>
          {label ? (
            <div className='flex items-center gap-2'>
              <FormLabel required={required} info={info}>
                {label}
              </FormLabel>
              {jsx ? <div className='pb-2'>{jsx}</div> : null}
            </div>
          ) : null}
          <div className={containerClassNames}>
            <textarea
              className='w-full focus:outline-none bg-transparent resize-none'
              ref={ref}
              style={{
                height: height ?? '90px',
              }}
              spellCheck
              lang='en-AU'
              {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            />
          </div>
          <FormError error={error} />
          {props?.helpertext ? <small className='text-muted-foreground'>{props?.helpertext}</small> : null}
        </div>
      )
    }
  )
)

export { Input, TextArea }
